/* ============================================================
   SpeedFly V6 — Product Page JS
   Variant picker, AJAX ATC, thumbnail gallery, quantity selector
   Zero jQuery dependency
   ============================================================ */

(function () {
  'use strict';

  class ProductForm {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.productJSON = this.getProductJSON();
      if (!this.productJSON) return;

      this.variants = this.productJSON.variants;
      this.options = this.productJSON.options;
      this.currentVariant = this.getInitialVariant();

      this.cacheDOM();
      this.bindEvents();
    }

    /* --- Data --- */

    getProductJSON() {
      const script = document.getElementById('ProductJSON-' + this.sectionId);
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (e) {
        console.error('[SpeedFly] Failed to parse product JSON:', e);
        return null;
      }
    }

    getInitialVariant() {
      const input = this.section.querySelector('[data-variant-id]');
      if (!input) return this.variants[0];
      const id = parseInt(input.value, 10);
      return this.variants.find((v) => v.id === id) || this.variants[0];
    }

    /* --- DOM Cache --- */

    cacheDOM() {
      this.form = this.section.querySelector('[data-product-form]');
      this.variantInput = this.section.querySelector('[data-variant-id]');
      this.optionInputs = this.section.querySelectorAll('[data-option-input]');
      this.atcButton = this.section.querySelector('[data-add-to-cart]');
      this.atcText = this.section.querySelector('[data-atc-text]');
      this.priceEl = this.section.querySelector('[data-product-price]');
      this.comparePriceEl = this.section.querySelector('[data-product-compare-price]');
      this.skuEl = this.section.querySelector('[data-product-sku]');
      this.urgencyEl = this.section.querySelector('[data-product-urgency]');
      this.thumbnails = this.section.querySelectorAll('[data-thumbnail]');
      this.featuredImage = this.section.querySelector('#ProductFeaturedImage');
      this.mediaViewer = this.section.querySelector('#ProductMedia-' + this.sectionId);
    }

    /* --- Events --- */

    bindEvents() {
      /* Option changes (radios, selects) */
      this.optionInputs.forEach((input) => {
        const event = input.tagName === 'SELECT' ? 'change' : 'change';
        input.addEventListener(event, () => this.onOptionChange());
      });

      /* Thumbnail clicks */
      this.thumbnails.forEach((thumb) => {
        thumb.addEventListener('click', () => this.onThumbnailClick(thumb));
      });

      /* AJAX ATC */
      if (this.form) {
        this.form.addEventListener('submit', (e) => this.onFormSubmit(e));
      }
    }

    /* --- Variant Matching --- */

    onOptionChange() {
      const selectedOptions = this.getSelectedOptions();
      const variant = this.getVariantFromOptions(selectedOptions);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.currentVariant = variant;
      this.updateVariantInput();
      this.updatePrice();
      this.updateATC();
      this.updateSKU();
      this.updateUrgency();
      this.updateMedia();
      this.updateURL();
      this.updateOptionLabels();

      SpeedFly.publish('variant:change', {
        variant,
        sectionId: this.sectionId,
      });
    }

    getSelectedOptions() {
      const options = [];
      this.section.querySelectorAll('.product__option').forEach((optionGroup) => {
        const radios = optionGroup.querySelectorAll('input[type="radio"]:checked');
        const selects = optionGroup.querySelectorAll('select');

        if (radios.length) {
          options.push(radios[0].value);
        } else if (selects.length) {
          options.push(selects[0].value);
        }
      });
      return options;
    }

    getVariantFromOptions(selectedOptions) {
      return this.variants.find((variant) => {
        return variant.options.every((opt, i) => opt === selectedOptions[i]);
      });
    }

    /* --- Updates --- */

    updateVariantInput() {
      if (this.variantInput) {
        this.variantInput.value = this.currentVariant.id;
      }
    }

    updatePrice() {
      if (!this.priceEl) return;
      const variant = this.currentVariant;
      const priceBlock = this.section.querySelector('#ProductPrice-' + this.sectionId);
      if (!priceBlock) return;

      const frFormat = '{{amount_with_comma_separator}}€';

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const savePercent = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );

        priceBlock.innerHTML = `
          <div class="product__price price">
            <span class="price--sale h3" data-product-price>${this.formatMoney(variant.price, frFormat)}</span>
            <span class="price--compare" data-product-compare-price>${this.formatMoney(variant.compare_at_price, frFormat)}</span>
            <span class="badge badge--sale">-${savePercent}%</span>
          </div>
        `;
      } else {
        priceBlock.innerHTML = `
          <div class="product__price price">
            <span class="price--regular h3" data-product-price>${this.formatMoney(variant.price, frFormat)}</span>
          </div>
        `;
      }

      /* Re-cache price elements after innerHTML */
      this.priceEl = this.section.querySelector('[data-product-price]');
      this.comparePriceEl = this.section.querySelector('[data-product-compare-price]');
    }

    updateATC() {
      if (!this.atcButton || !this.atcText) return;
      const variant = this.currentVariant;
      const frFormat = '{{amount_with_comma_separator}}€';

      if (variant.available) {
        this.atcButton.disabled = false;
        this.atcText.innerHTML =
          (window.cartStrings?.addToCart || 'Add to cart') +
          ' | ' +
          this.formatMoney(variant.price, frFormat);
      } else {
        this.atcButton.disabled = true;
        this.atcText.textContent = window.cartStrings?.soldOut || 'Sold out';
      }
    }

    updateSKU() {
      if (!this.skuEl) return;
      const sku = this.currentVariant.sku;
      if (sku) {
        this.skuEl.textContent = 'SKU: ' + sku;
        this.skuEl.hidden = false;
      } else {
        this.skuEl.hidden = true;
      }
    }

    updateUrgency() {
      if (!this.urgencyEl) return;
      const variant = this.currentVariant;

      /* We can't get inventory from JS alone (Shopify doesn't expose it in product JSON
         by default unless inventory_quantity is explicitly included via variant).
         The urgency block is rendered server-side. For variant changes,
         we hide urgency since we can't reliably check stock per variant client-side.
         A store using inventory tracking can add this via a section rendering API call. */
      this.urgencyEl.hidden = true;
    }

    updateMedia() {
      if (!this.currentVariant.featured_image) return;
      const img = this.currentVariant.featured_image;

      /* Desktop: update main featured image */
      if (this.featuredImage) {
        this.featuredImage.src = this.getSizedImageUrl(img.src, '1100x');
        this.featuredImage.srcset =
          this.getSizedImageUrl(img.src, '375x') + ' 375w, ' +
          this.getSizedImageUrl(img.src, '550x') + ' 550w, ' +
          this.getSizedImageUrl(img.src, '750x') + ' 750w, ' +
          this.getSizedImageUrl(img.src, '1100x') + ' 1100w';
        this.featuredImage.alt = img.alt || '';
      }

      /* Desktop: update active thumbnail */
      this.thumbnails.forEach((thumb) => {
        const mediaId = thumb.dataset.mediaId;
        const isActive = parseInt(mediaId, 10) === img.id;
        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      /* Mobile: sync native scroll-snap gallery to variant image */
      this.syncMobileGalleryToVariant(img.id);
    }

    syncMobileGalleryToVariant(mediaId) {
      /* Find the native scroll-snap track */
      const track = this.section.querySelector('[data-mobile-track]');
      if (!track) return;

      /* Find the active thumbnail index to sync mobile gallery */
      let targetIndex = 0;
      this.thumbnails.forEach((thumb, i) => {
        if (thumb.classList.contains('is-active')) {
          targetIndex = i;
        }
      });

      const slides = track.querySelectorAll('.product__mobile-slide');
      if (slides[targetIndex]) {
        track.scrollTo({ left: slides[targetIndex].offsetLeft, behavior: 'smooth' });
      }
    }

    updateURL() {
      if (!this.currentVariant || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', this.currentVariant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updateOptionLabels() {
      /* Update active states on pills/swatches */
      this.section.querySelectorAll('.product__pill, .product__swatch').forEach((label) => {
        const input = label.querySelector('input[type="radio"]');
        if (input) {
          label.classList.toggle('is-active', input.checked);
        }
      });
    }

    setUnavailable() {
      if (this.atcButton) this.atcButton.disabled = true;
      if (this.atcText) this.atcText.textContent = window.cartStrings?.unavailable || 'Unavailable';
    }

    /* --- Thumbnail Gallery --- */

    onThumbnailClick(thumb) {
      const src = thumb.dataset.mediaSrc;
      if (!src || !this.featuredImage) return;

      /* Swap main image */
      this.featuredImage.src = src;
      this.featuredImage.srcset = '';

      /* Update active thumbnail */
      this.thumbnails.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-current', 'false');
      });
      thumb.classList.add('is-active');
      thumb.setAttribute('aria-current', 'true');
    }

    /* --- AJAX Add to Cart --- */

    async onFormSubmit(e) {
      e.preventDefault();

      if (!this.currentVariant || !this.currentVariant.available) return;

      const quantityInput = this.form.querySelector('[name="quantity"]');
      const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

      this.setLoading(true);

      try {
        await SpeedFly.cart.add([
          {
            id: this.currentVariant.id,
            quantity: quantity,
          },
        ]);

        /* Update cart count */
        const cart = await SpeedFly.cart.get();
        SpeedFly.updateCartCount(cart.item_count);

        /* Announce for screen readers */
        SpeedFly.announce(
          (window.cartStrings?.addedToCart || 'Added to cart') +
          ': ' + this.productJSON.title
        );

        /* Publish event — cart drawer / popup can listen */
        SpeedFly.publish('cart:add', {
          variant: this.currentVariant,
          quantity: quantity,
          cart: cart,
        });

      } catch (err) {
        console.error('[SpeedFly] ATC error:', err);
        SpeedFly.announce(err.message || window.cartStrings?.cartError || 'Error adding to cart');
      } finally {
        this.setLoading(false);
      }
    }

    setLoading(isLoading) {
      if (!this.atcButton) return;
      this.atcButton.classList.toggle('btn--loading', isLoading);
      this.atcButton.disabled = isLoading;
    }

    /* --- Money Formatting --- */

    formatMoney(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.floor(cents / 100);
      const amountWithComma = amount.replace('.', ',');

      let value = '';

      switch (format.match(/\{\{(\w+)\}\}/)?.[1]) {
        case 'amount':
          value = format.replace(
            /\{\{amount\}\}/,
            this.numberWithCommas(amount)
          );
          break;
        case 'amount_no_decimals':
          value = format.replace(
            /\{\{amount_no_decimals\}\}/,
            this.numberWithCommas(amountNoDecimals)
          );
          break;
        case 'amount_with_comma_separator':
          value = format.replace(
            /\{\{amount_with_comma_separator\}\}/,
            amountWithComma.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
          );
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = format.replace(
            /\{\{amount_no_decimals_with_comma_separator\}\}/,
            String(amountNoDecimals).replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.')
          );
          break;
        default:
          value = format.replace(/\{\{\w+\}\}/, this.numberWithCommas(amount));
      }

      return value;
    }

    numberWithCommas(num) {
      const parts = num.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return parts.join('.');
    }

    getSizedImageUrl(src, size) {
      if (!src) return '';
      /* Shopify CDN format: replace _widthxheight. or insert before extension */
      return src.replace(/\.(\w+)(\?|$)/, '_' + size + '.$1$2');
    }
  }

  /* ============================================================
     Sticky ATC — Shows fixed bottom bar on mobile when main ATC
     scrolls out of viewport
     ============================================================ */
  class StickyATC {
    constructor(section) {
      this.section = section;
      this.sectionId = section.dataset.sectionId;
      this.stickyBar = document.getElementById('StickyATC-' + this.sectionId);
      this.mainATC = section.querySelector('[data-add-to-cart]');
      if (!this.stickyBar || !this.mainATC) return;

      this.stickyBtn = this.stickyBar.querySelector('[data-sticky-atc-btn]');
      this.isVisible = false;

      this.initObserver();
      this.bindEvents();
    }

    initObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const shouldShow = !entry.isIntersecting;
            if (shouldShow !== this.isVisible) {
              this.isVisible = shouldShow;
              this.stickyBar.classList.toggle('is-visible', shouldShow);
              this.stickyBar.setAttribute('aria-hidden', String(!shouldShow));
              /* Coordinate with bottom nav via body class */
              document.body.classList.toggle('sticky-atc-visible', shouldShow);
            }
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );
      this.observer.observe(this.mainATC);
    }

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      document.body.classList.remove('sticky-atc-visible');
    }

    bindEvents() {
      if (!this.stickyBtn) return;
      this.stickyBtn.addEventListener('click', () => {
        /* Trigger the main form submit */
        const form = this.section.querySelector('[data-product-form]');
        if (form) {
          form.dispatchEvent(new Event('submit', { cancelable: true }));
        }
      });

      /* Update sticky price/availability on variant change */
      if (typeof SpeedFly !== 'undefined' && SpeedFly.subscribe) {
        SpeedFly.subscribe('variant:change', ({ variant }) => {
          this.updateSticky(variant);
        });
      }
    }

    updateSticky(variant) {
      if (!variant) return;
      const priceEl = this.stickyBar.querySelector('[data-sticky-price]');
      if (priceEl) {
        const amount = (variant.price / 100).toFixed(2).replace('.', ',');
        priceEl.textContent = amount + '€';
      }
      if (this.stickyBtn) {
        this.stickyBtn.disabled = !variant.available;
        this.stickyBtn.textContent = variant.available
          ? (window.cartStrings?.addToCart || 'Add to cart')
          : (window.cartStrings?.soldOut || 'Sold out');
      }
    }
  }

  /* === INIT === */
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-section-type="product"], .section-main-product [data-section-id]').forEach((section) => {
      if (section.querySelector('[data-product-form]')) {
        new ProductForm(section);
      }
    });

    /* Also match the section wrapper */
    const productSection = document.querySelector('.section-main-product');
    if (productSection) {
      const inner = productSection.querySelector('[data-section-id]');
      if (inner && inner.querySelector('[data-product-form]') && !inner._productForm) {
        inner._productForm = new ProductForm(inner);
      }
    }

    /* Init Sticky ATC */
    document.querySelectorAll('[data-section-id]').forEach((section) => {
      if (document.getElementById('StickyATC-' + section.dataset.sectionId)) {
        new StickyATC(section);
      }
    });

    /* Gallery swipe: handled by native CSS scroll-snap (inline in main-product.liquid) */
  });
})();

